import React, { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, CalendarPlus } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import TodayBoard, { call as todayCall } from '../components/TodayBoard';
import { MyProjects } from './TeamBoard';

// One person's own board. "Hi Rachael" at the top, their claimed team projects, everything
// else in the company already pointed at them, and their own list.
//
// ⚠️ THE SECOND SECTION IS THE POINT OF THE PAGE. Most of these people already had work
// recorded against them on the accounting, procurement, contact centre and marketing boards -
// what nobody had was a screen that showed one person all of theirs at once. That is why it
// groups by the board it came from rather than flattening: where a thing lives is how they
// find it again.
//
// ⚠️ It shows only your own work, and that is a GATE not a filter - the owner is resolved
// server side from the session and there is no parameter to change.

const base = () => (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions');

async function call(action, params = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/my-board', {
    method: 'POST', headers, body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || 'my-board ' + res.status); e.status = res.status; throw e; }
  return data;
}

// Morning, afternoon or evening in Eastern - the business runs on Crisfield time, and a
// greeting that says good morning at 7pm because the browser is elsewhere reads as broken.
function partOfDay() {
  const h = Number(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', hour12: false,
  }).format(new Date()));
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function MyBoard({ navigateTo }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [todayKey, setTodayKey] = useState(0);

  const load = useCallback(async () => {
    try { setData(await call('board')); setErr(null); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (action, params) => {
    setBusy(true);
    try { setData(await call(action, params)); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <div className="page-content" style={{ padding: 28 }}>
        {err ? <div className="card"><div className="card-body">{err}</div></div> : 'Loading your board...'}
      </div>
    );
  }

  const me = data.me;
  // Put one of your own tasks on today's board. Greg turned the planner on for the whole team
  // on 2026-09-25; the server scopes person_tasks to the signed-in owner.
  const putOnToday = async (id) => {
    try { await todayCall('place', { srcTable: 'person_tasks', srcId: String(id) }); setTodayKey(k => k + 1); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div className="page-content" style={{ padding: '22px 28px 40px', maxWidth: 1100 }}>

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: 'var(--navy)' }}>
          {partOfDay()}, {me.first}
        </h1>
        <p style={{ margin: '7px 0 0', fontSize: 14, color: 'var(--muted, #59707E)' }}>
          {data.claimed.length > 0 && <>{data.claimed.length} project{data.claimed.length === 1 ? '' : 's'} claimed &middot; </>}
          {data.elsewhereCount} thing{data.elsewhereCount === 1 ? '' : 's'} pointed at you across the company &middot; {data.openCount} on your own list
        </p>
      </div>

      {err && (
        <div style={{ background: '#FDECE6', border: '1px solid #F4C3AE', borderRadius: 10, padding: '11px 15px', marginBottom: 18, fontSize: 13.5, color: '#8A3312' }}>{err}</div>
      )}

      {/* 0. Today: the same day planner Greg runs his day from - set the day, pull things into a
          schedule, drag to reorder, tick them into Accomplished. */}
      <TodayBoard refreshKey={todayKey} onChanged={load} />

      {/* 1. What they took off the team board */}
      <MyProjects onChanged={load} navigateTo={navigateTo} />

      {/* 2. Their own list */}
      <Section title="Your own list" note="yours alone, and nobody else sees it here">
        <form onSubmit={(e) => { e.preventDefault(); const v = draft.trim(); if (!v || busy) return; setDraft(''); act('add', { text: v }); }}
          style={{ marginBottom: 12 }}>
          <input className="cpx-touch" value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="+ Add something to your list..."
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', fontSize: 14, border: '1px solid var(--line, #D9E2E8)', borderRadius: 10, fontFamily: 'inherit' }} />
        </form>
        {data.tasks.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--muted, #59707E)' }}>Nothing on it. Type above and press enter.</div>
        ) : (
          <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 14, padding: '10px 16px' }}>
            {data.tasks.map(t => (
              <label key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid #F4F7F9' }}>
                <input type="checkbox" checked={t.done} onChange={() => act('toggle', { id: t.id })}
                  style={{ width: 17, height: 17, accentColor: 'var(--orange)', marginTop: 2, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45, color: t.done ? '#93A6B1' : 'var(--ink, #0B1F2A)', textDecoration: t.done ? 'line-through' : 'none' }}>{t.text}</span>
                {!t.done && (
                  <button onClick={(e) => { e.preventDefault(); putOnToday(t.id); }} title="Put on today's board"
                    style={{ border: 'none', background: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0 }}>
                    <CalendarPlus size={15} />
                  </button>
                )}
                <button onClick={(e) => { e.preventDefault(); act('remove', { id: t.id }); }} title="Remove"
                  style={{ border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, opacity: 0.6 }}>
                  <X size={14} />
                </button>
              </label>
            ))}
          </div>
        )}
      </Section>

      {/* 3. Everything already recorded against them, anywhere */}
      <Section title="Already pointed at you" note="from every board in the company, so nothing is only remembered in somebody else's screen">
        {data.elsewhere.length === 0 ? (
          <div style={{ fontSize: 13.5, color: 'var(--muted, #59707E)' }}>Nothing open anywhere else.</div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {data.elsewhere.map(g => (
              <div key={g.domain} style={{ background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 14, padding: '15px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 9, marginBottom: 9 }}>
                  <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--navy)' }}>{g.domain}</h3>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted, #59707E)' }}>{g.items.length}</span>
                </div>
                <div style={{ display: 'grid', gap: 7 }}>
                  {g.items.map(it => (
                    <div key={it.srcTable + it.srcId} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <ExternalLink size={13} color="var(--muted, #59707E)" style={{ marginTop: 3, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>{it.title}</div>
                        {it.detail && <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--muted, #59707E)', marginTop: 3 }}>{it.detail}</div>}
                      </div>
                      {it.due && (
                        <span style={{ fontFamily: 'monospace', fontSize: 11.5, color: 'var(--orange)', whiteSpace: 'nowrap', marginTop: 2 }}>{it.due}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

const Section = ({ title, note, children }) => (
  <div style={{ marginBottom: 30 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 13 }}>
      <h2 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--navy)' }}>{title}</h2>
      <span style={{ fontSize: 13, color: 'var(--muted, #59707E)' }}>{note}</span>
    </div>
    {children}
  </div>
);
